import React, { cloneElement, useEffect, useMemo, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { FolderClosedIcon, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';
import { LoadingOverlay } from '../common/loading-overlay';
import { KnowledgeFolderData } from '@/services/knowledge-list-folders';
import { Image } from '../common/image';
import knowledgeBg from '@/assets/assets/knowledge-bg.webp';
import { CheckedDisabledIcon, CheckedIcon, UncheckedIcon } from '../assets-knowledge-item/assets';
import { TruncatedText } from '../common/truncated-text';
import { assetsKnowledgeHooks, assetsKnowledgeItemHooks } from '@/manager';
import fileBg from '@/assets/assets/file-bg.webp'
import { FileBgIcon } from '../agent-dashboard-order-management/assets';
import { Skeleton } from '../ui/skeleton';

interface DocumentChooseModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  knowledgeFolders?: KnowledgeFolderData[];
  title?: string;
  loading?: boolean;
  disabledIds?: number[];
  multiple?: boolean;
  onConfirm?: (knowledgeItems: { id: number; parentId: number; name: string }[]) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 文档选择弹窗 */
export const DocumentChooseModal: React.FC<DocumentChooseModalProps> = ({
  trigger,
  open: externalOpen,
  title,
  // knowledgeFolders,
  loading,
  disabledIds,
  multiple,
  onConfirm,
  onOpenChange,
}) => {
  const { t } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // 知识库文件夹
  const { knowledgeFolders } = assetsKnowledgeHooks.useAssetsKnowledgeData();
  // 知识库文件
  const { knowledgeItems, loading: knowledgeItemsLoading, fetchKnowledgeItems } = assetsKnowledgeItemHooks.useFetchKnowledgeItems();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (open) {
      if (knowledgeFolders?.length > 0) {
        setActiveTab(knowledgeFolders[0].id);
        fetchKnowledgeItems(knowledgeFolders[0].id);
      }
      setSelectedIds([]);
    } else {
      setSelectedIds([]);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  useEffect(() => {
    if (!activeTab && knowledgeFolders?.length > 0) {
      setActiveTab(knowledgeFolders[0].id);
      fetchKnowledgeItems(knowledgeFolders[0].id);
    }
  }, [knowledgeFolders, activeTab]);

  // 左侧导航
  const tabs = useMemo(() => {
    return knowledgeFolders.map(folder => ({
      id: folder.id,
      label: folder.folderName,
    }));
  }, [knowledgeFolders]);

  // 选择标签
  const handleSelect = (id: number) => {
    if (!multiple) {
      setSelectedIds([id]);
      return;
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    }
  }

  // 确认选择
  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    await onConfirm?.(knowledgeItems.filter(item => selectedIds.includes(item.id)).map(item => {
      return {
        id: item.id,
        parentId: item.parentId,
        name: item.contentTitle,
      }
    }));
    handleClose();
  }

  const handleTabChange = (id: number) => {
    setActiveTab(id);
    fetchKnowledgeItems(id);
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-black/50 z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] max-h-[544px] min-h-[544px] flex-shrink-0",
          "z-[50] overflow-hidden"
        )}
      >
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b border-[rgba(234,236,240,1)]">
          <Dialog.Title
            className='focus:outline-none text-[rgba(16,24,40,1)] text-base font-semibold flex items-center'
          >
            <span>{t("agent_creation.input.document_modal.title")}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              icon={<X className="size-6 text-[rgba(71,84,103,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        <div className='flex-1 flex select-none min-h-0'>
          {/* 左侧导航 */}
          <div className="w-[280px] shrink-0 p-3 border-r border-[rgba(234,236,240,1)] flex flex-col gap-1 overflow-y-auto transition-all">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className={cn(
                  'flex items-center gap-2 rounded-[6px] px-3 py-2 cursor-pointer',
                  activeTab === tab.id
                    ? 'bg-[rgba(16,24,40,0.05)]'
                    : 'hover:bg-[rgba(16,24,40,0.05)]'
                )}
                onClick={() => handleTabChange(tab.id)}
              >
                <Image src={knowledgeBg} className="size-6" />
                <TruncatedText text={tab.label} className='w-[90%] text-[rgba(16,24,40,1)] text-sm font-medium' />
              </div>
            ))}
          </div>
          {/* 右侧内容区域 */}
          <div className='flex-1 flex flex-col min-w-0'>
            <div className='flex-1 overflow-y-auto p-3 flex flex-col gap-1'>
              {/* 知识库文件夹列表 */}
              {knowledgeItemsLoading && <DocumentSkeleton />}
              {!knowledgeItemsLoading && knowledgeItems?.length > 0 && knowledgeItems?.map(item => {
                const type = item.itemUrl ? item.itemUrl.split('.').pop() || '' : 'md';
                return (
                  <div
                    className={cn(
                      'w-full h-[55px] rounded-[8px] p-3 flex items-center gap-3 transition-all hover:bg-[rgba(16,24,40,0.05)] cursor-pointer',
                      disabledIds?.includes(item.id) && 'cursor-not-allowed opacity-75',
                      selectedIds.includes(item.id) && 'bg-[rgba(16,24,40,0.05)]'
                    )}

                    onClick={() => {
                      if (disabledIds?.includes(item.id)) return;
                      handleSelect(item.id)
                    }}
                  >
                    {disabledIds?.includes(item.id) ?
                      <CheckedDisabledIcon className="size-5 flex-shrink-0" />
                      : selectedIds.includes(item.id) ? (
                        <CheckedIcon className="size-5 flex-shrink-0" />
                      ) : (
                        <UncheckedIcon className="size-5 flex-shrink-0" />
                      )}
                    <div
                      className='relative select-none'
                    >
                      {type !== 'md' && <FileBgIcon className='w-8 h-10' />}
                      {type === 'md' && <Image src={fileBg} className='w-8 h-10' />}
                      <div className={cn(
                        'absolute top-4 -left-0.5 px-[3.4px] py-[1px] rounded-[5px] !text-[9px] font-semibold text-white bg-[rgba(55,93,251,1)] tracking-wide',
                        type === 'pdf' && 'bg-[rgba(223,28,65,1)]',
                      )}>
                        {type.toLocaleUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                      <TruncatedText
                        text={item.contentTitle || t('assets.knowledge_item.untitled_note')}
                        className="w-[92%] text-[rgba(16,24,40,1)] text-base font-medium"
                      />
                      {/* <TruncatedText
                        text="Today memorized Red Book Unit12, 150 words"
                        className="w-[92%] text-[rgba(102,112,133,1)] text-xs"
                      /> */}
                    </div>
                  </div>
                )
              })}
              {/* 空状态处理：当选择页签没有数据时 */}
              {!knowledgeItemsLoading && knowledgeItems?.length <= 0 && (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm py-10">
                  <FolderClosedIcon className="size-10 mb-2 opacity-20" />
                  <p>{t('agent_creation.input.document_modal.no_file')}</p>
                </div>
              )}
            </div>
            <div className='shrink-0 flex justify-end gap-3 border-t px-3 py-3'>
              <TextButton
                label={t("agent_creation.input.document_modal.cancel")}
                className='max-w-max text-sm  h-9 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold !text-[rgba(65,70,81,1)] !px-10'
                onClick={handleClose}
              />
              <TextButton
                label={t("agent_creation.input.document_modal.confirm")}
                className='max-w-max text-sm font-semibold h-9 !px-10'
                disabled={selectedIds.length === 0}
                onClick={handleConfirm}
              />
            </div>
          </div>
        </div>
        <LoadingOverlay loading={loading} />
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

/** 文档选择骨架屏组件 */
const DocumentSkeleton = () => {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="w-full h-[55px] rounded-[8px] p-3 flex items-center gap-3 animate-pulse"
        >
          {/* 复选框骨架 */}
          <Skeleton className="size-5 flex-shrink-0 rounded-full" />

          {/* 文件图标骨架 */}
          <div className="relative select-none">
            <Skeleton className="w-8 h-10 rounded-[4px]" />
          </div>

          {/* 文件信息骨架 */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <Skeleton className="h-6 w-3/4 rounded" />
          </div>
        </div>
      ))}
    </>
  );
};